/**
 * 顶栏组件（回退按钮 + 面包屑 + 帮助信息按钮）——按 docs/ui_topbar 模板 01 移植
 * 设计要点：
 * 1. 由 Layout 统一渲染, 各页面无需自带回退按钮
 * 2. 回退按"面包屑倒数第二级"跳转, 而非浏览器历史
 * 3. 首页 (面包屑只有一级) 时回退按钮置灰禁用
 * 4. 路径通过正则匹配 location.pathname 自动生成
 * 5. 帮助信息通过 ROUTE_HELP 映射, 点击 info 图标弹出 popover
 */
import { cloneElement, useMemo, type ReactNode } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  makeStyles,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbButton,
  BreadcrumbDivider,
  Button,
  Tooltip,
  InfoLabel,
  Text,
  tokens,
} from '@fluentui/react-components'
import { ArrowLeftRegular, HomeRegular } from '@fluentui/react-icons'
import { ROUTES } from '@/routes'
import { NAV, NAV_TOP } from '@/nav/schema'

const useStyles = makeStyles({
  root: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    height: '100%',
    minWidth: 0,
  },
  backButton: { flexShrink: 0 },
  breadcrumbWrap: { minWidth: 0, overflow: 'hidden', flex: 1 },
  helpButton: {
    flexShrink: 0,
    // README §4.5：InfoLabel 根是 inline-block Label，info 图标按文本基线对齐偏下，
    // 显式改为 flex 容器垂直居中（对齐 ming-git 定稿效果）
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    alignSelf: 'center',
    lineHeight: 1,
  },
  helpPopover: {
    maxWidth: '420px',
    padding: '12px 14px',
    color: tokens.colorNeutralForeground1,
    fontSize: tokens.fontSizeBase300,
    lineHeight: '1.5',
  },
})

interface CrumbItem {
  label: ReactNode
  key: string
  route?: string
}

// ===== 路由 → 帮助文案映射 =====
const ROUTE_HELP: Record<string, ReactNode> = {
  [ROUTES.HOME]: '系统总览与关键指标。',
  [ROUTES.DOMAINS]: '收件域的集中管理：选择左侧域名后，右侧 Tab 可配置该域名的邮箱账号、别名与转发、DKIM 签名、TLS 安全与 SMTP/收信设置。新增/编辑后立即生效。',
  [ROUTES.USERS]: '邮箱账号管理：创建用户、配额与启用状态。',
  [ROUTES.ALIASES]: '邮箱别名与转发规则。',
  [ROUTES.QUEUE]: '跨域投递队列：查看、重试与删除待投递邮件。',
  [ROUTES.BACKUPS]: '数据备份：创建、下载与删除备份文件。',
  [ROUTES.AUDIT_LOGS]: '管理操作审计记录。',
  [ROUTES.CLIENTS]: 'SMTP/API 客户端接入与审核。',
  [ROUTES.DKIM]: (
    <>
      <p>
        DKIM 是<b>发信域签名</b>：用你域名的私钥给发出的邮件「头 + 正文」加数字签名，收件方服务器用你发布的公钥（TXT 记录）验证，证明邮件确实由你授权的服务器发出、且中途未被篡改。
      </p>
      <p>
        有自己域名、要对外发信时通常<b>必须配置</b>——外网收件方（Gmail / QQ / Outlook 等）会校验 SPF + DKIM + DMARC，缺 DKIM 大概率进垃圾箱或被拒；DKIM 记录必须挂在你的域名 DNS 上，所以有域名正是配置它的前提。
      </p>
      <p>
        仅当<b>纯收信不对外发信</b>、仅服务器内部同域投递、或仅内部 / 测试环境（对方不校验 DKIM）时才可跳过。
      </p>
      <p>
        <b>Selector</b> 是 DNS 中定位公钥记录的主机名前缀，最终记录名为 <code>{"<selector>._domainkey.<域名>"}</code>，可自行起名，但需与域名服务商处发布的 TXT 记录保持一致。
      </p>
    </>
  ),
  [ROUTES.TLS]: 'TLS 证书配置与热重载（无需重启服务）。',
  [ROUTES.MAIL_TEST]: '以指定邮箱身份发送邮件并检查回执。',
  [ROUTES.SEND_LOGS]: '邮件发送记录与状态。',
  [ROUTES.TEMPLATES]: '网关发信模板与变量定义。',
  [ROUTES.ANTISPAM]: '反垃圾设置：拒收域名黑名单、DNSBL 与入站间隔，保存后实时生效，无需重启服务。',
}

// ===== 路由 → 面包屑标题映射 =====
const routeTitles: Record<string, string> = {
  [ROUTES.DOMAINS]: '收件域',
  [ROUTES.USERS]: '邮箱账号',
  [ROUTES.ALIASES]: '别名与转发',
  [ROUTES.QUEUE]: '队列管理',
  [ROUTES.BACKUPS]: '备份管理',
  [ROUTES.AUDIT_LOGS]: '审计日志',
  [ROUTES.CLIENTS]: '接入客户端',
  [ROUTES.DKIM]: 'DKIM 签名',
  [ROUTES.TLS]: 'TLS 安全',
  [ROUTES.MAIL_TEST]: '写信发送',
  [ROUTES.SEND_LOGS]: '发送记录',
  [ROUTES.TEMPLATES]: '邮件模板',
  [ROUTES.SETTINGS]: '设置',
  [ROUTES.ANTISPAM]: '反垃圾设置',
}

// ===== 面包屑图标：来自侧栏 nav/schema.tsx（README §1.2#4 / §6 / 附录B，不再独立维护映射）=====
const schemaIconByPath: Record<string, ReactNode> = Object.fromEntries(
  [NAV_TOP, ...NAV.flatMap((g) => g.items)].map((n) => [n.path, n.icon]),
)
/** 复用 schema 的 20px 菜单图标，等比缩至 14px（README §5.1） */
const renderIcon = (path: string): ReactNode => {
  const icon = schemaIconByPath[path]
  if (!icon) return null
  return cloneElement(icon as React.ReactElement, { width: 14, height: 14 })
}

/**
 * 通用顶栏组件：左侧"返回上一级"按钮 + 中间面包屑路径 + 右侧帮助信息按钮。
 */
export default function PageBreadcrumb() {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()

  // 面包屑项: 页面菜单图标(14px, 来自 schema) + 文字（须在 useMemo 之前定义，避免 const 暂存死区 TDZ）
  const crumbLabel = (path: string, text: string) => {
    const icon = renderIcon(path)
    return (
      <>
        {icon && (
          <span style={{ display: 'inline-flex', verticalAlign: 'middle', marginRight: 4 }}>
            {icon}
          </span>
        )}
        {text}
      </>
    )
  }

  const crumbs = useMemo<CrumbItem[]>(() => {
    const p = location.pathname

    // 首页 - 只有一级, 回退按钮禁用（README §1.2#1: p === ROUTES.home）
    if (p === ROUTES.HOME) {
      return [{ label: NAV_TOP.label, key: 'home' }]
    }

    // 详情页正则: /domains/:id 等（README §4.2 详情分支）
    const detailMatch = p.match(/^\/(domains|users|aliases|queue|backups|audit-logs|clients|dkim|send-logs|templates)\/([^/]+)/)
    if (detailMatch) {
      const section = `/${detailMatch[1]}`
      const title = routeTitles[section] ?? '详情'
      return [
        { label: <HomeRegular />, key: 'home', route: ROUTES.HOME },
        { label: crumbLabel(section, title), key: section, route: section },
        { label: decodeURIComponent(detailMatch[2]), key: 'detail' },
      ]
    }

    // 其他顶层页面
    const title = routeTitles[p] ?? '页面'
    return [
      { label: <HomeRegular />, key: 'home', route: ROUTES.HOME },
      { label: crumbLabel(p, title), key: 'title' },
    ]
  }, [location.pathname])

  // 帮助文案: 从 ROUTE_HELP 映射取当前路由对应的说明（父级继承）
  const helpInfo = useMemo(() => {
    const p = location.pathname
    if (ROUTE_HELP[p]) return ROUTE_HELP[p]
    const m = p.match(/^\/(domains|users|aliases|queue|backups|audit-logs|clients|dkim|send-logs|templates)\//)
    if (m) return ROUTE_HELP[`/${m[1]}`] ?? null
    return null
  }, [location.pathname])

  // 回退: 跳到倒数第二级; 只有一级时禁用
  const canGoBack = crumbs.length >= 2
  const onBack = () => {
    if (!canGoBack) return
    const target = crumbs[crumbs.length - 2]
    if (target?.route) {
      navigate(target.route)
    } else {
      navigate(ROUTES.HOME)
    }
  }

  return (
    <div className={styles.root}>
      <Tooltip content={canGoBack ? '返回' : '已在首页'} relationship="label">
        <Button
          appearance="subtle"
          size="small"
          icon={<ArrowLeftRegular />}
          className={styles.backButton}
          onClick={onBack}
          disabled={!canGoBack}
          aria-label="返回上一级"
        />
      </Tooltip>
      <div className={styles.breadcrumbWrap}>
        <Breadcrumb size="small">
          {crumbs.map((item, i) => (
            <BreadcrumbItem key={item.key}>
              {i > 0 && <BreadcrumbDivider />}
              <BreadcrumbButton
                onClick={() => {
                  if (item.route) navigate(item.route)
                }}
              >
                {item.label}
              </BreadcrumbButton>
            </BreadcrumbItem>
          ))}
        </Breadcrumb>
      </div>
      {helpInfo && (
        <InfoLabel
          className={styles.helpButton}
          info={<div className={styles.helpPopover}>{helpInfo}</div>}
          size="small"
        />
      )}
    </div>
  )
}

/** 页面标题组件（独立导出，页面内容区 H1） */
export function PageTitle({ children, subtitle }: { children: ReactNode; subtitle?: string }) {
  return (
    <div style={{ marginBottom: '16px' }}>
      <Text as="h1" size={500} weight="semibold" block>{children}</Text>
      {subtitle && (
        <Text size={300} style={{ color: tokens.colorNeutralForeground3 }} block>
          {subtitle}
        </Text>
      )}
    </div>
  )
}
